<template>
  <div class="main-container">
    <Header
      title="Nueva llamada de Servicio"
      sub-title="Formulario para registrar una nueva solicitud de servicio"
    />

    <Breadcrumbs
      style="max-width: 780px;"
      :steps="steps"
      :current-step="currentStep"
      @step-clicked="goToStep"
    />

    <div class="step-body">
      <transition name="step-fade" mode="out-in">
        <div class="card">
          <FirstStep
            ref="firstStep"
            v-show="currentStep === 0"
            :form="{ client: form.client, contact: form.contact, address: form.address }"
          />
          <SecondStep
            ref="secondStep"
            v-show="currentStep === 1"
            :form="{
              service: form.service,
              attachments: form.attachments
            }"
            @order-resolved="handleOrderResolvedFromTs"
          />
          <ThirdStep
            ref="thirdStep"
            v-show="currentStep === 2"
            :form="{ service: form.service, client: form.client }"
            @clear-all="form.attachments = []"
          />
        </div>
      </transition>

      <StepFooter
        :current-step="currentStep"
        :steps="steps"
        @step-clicked="handleStepClick"
        @create-order="handleCreateOrder"
      />
    </div>

    <ConfirmModal
      v-if="showSubmitModal"
      :processing="false"
      required
      :disabled="invalidSubmit"
      :modal-width="'480px'"
      @close="onHideSubmitModal"
      @onConfirm="createServiceCall"
    >
      <template #header>
        <h5 class="mb-1">
          <span v-if="confirmCreateOnResolvedTsStatus == 'pending'">Registrar OS</span>
          <span v-else>{{ submitAction == 'create_os' ? 'Orden de Servicio' : 'Solicitud de OS' }}</span>
        </h5>
      </template>

      <template #body>
        <template v-if="creating">
          <div class="text-center py-3">
            <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem;" />
            <p class="mt-2 mb-1 font-weight-500 fs-17 text-primary">
              {{ submitAction === 'create_os' ? 'registrando Orden de Servicio...' : 'Registrando Solicitud de OS...' }}
            </p>
            <p class="text-muted small mb-0">El proceso puede tardar unos segundos</p>
          </div>
        </template>

        <template v-else-if="creationStatus == 'success'">
          <div class="text-center py-2">
            <div class="text-success mx-auto mb-2" style="font-size: 25px;">
              <font-awesome-icon :icon="['fas', 'check-circle']" fixed-width size="2xl" />
            </div>
            <p class="text-success mb-2 fs-18">
              <span v-if="confirmCreateOnResolvedTsStatus == 'pending' && submitAction === 'create_sos'">
                Orden de Servicio registrada correctamente
              </span>
              <span v-else>
                {{ submitAction === 'create_os' ? 'Orden de Servicio registrada en SAP correctamente' : 'Solicitud de OS registrada correctamente' }}
              </span>
            </p>
            <p class="mb-1 fs-15">
              <span v-if="submitAction === 'create_os'">
                Número de OS:
                <router-link :to="{ path: `/app/prod/llamadas-servicio/${serviceCallCreated.id}` }" custom v-slot="{ href, navigate }">
                  <a :href="href" target="_blank" rel="noopener noreferrer" @click="navigate">
                    <b>{{ serviceCallCreated.sap_doc_num }}</b>
                  </a>
                </router-link>
              </span>
              <span v-else-if="confirmCreateOnResolvedTsStatus != 'pending'">
                Se notificó a los gestores de Post-venta para que continúen con el proceso
              </span>
            </p>
          </div>
        </template>

        <template v-else-if="creationStatus == 'error'">
          <div class="text-center py-2">
            <div class="text-danger mx-auto mb-2" style="font-size: 25px;">
              <font-awesome-icon :icon="['far', 'times-circle']" fixed-width size="2xl" />
            </div>
            <p class="text-danger mb-1 fs-18">
              <span v-if="confirmCreateOnResolvedTsStatus == 'pending'">
                Error al registrar la orden de servicio
              </span>
              <span>
                Error al registrar la {{ submitAction === 'create_os' ? 'Orden de servicio' : 'Solicitud de OS' }}
              </span>
            </p>
            <p class="text-muted fs-15 mb-1">
              Por favor, inténtelo de nuevo o contacte a Soporte Técnico
            </p>
          </div>
        </template>


        <template v-else-if="formValidation.errors.length > 0">
          <div v-if="formValidation.sos_valid && confirmCreateOnResolvedTsStatus == 'pending'">
            <b-alert show variant="info" class="text-center" style="line-height: 1.1875rem;">
              <span class="fs-15">Se ha marcado como solucionado el problema en el troubleshooting</span>
            </b-alert>
            <p class="mb-0 font-weight-bold">
              ¿Desea registrar la OS ahora o continuar con el registro?
            </p>
          </div>
          <div v-else>
            <p class="text-left small mb-2 fs-15 font-weight-bold">
              <span v-if="formValidation.sos_valid && !formValidation.os_valid">
                Antes de continuar, debe completar los siguientes requisitos:
              </span>
              <span v-else>Para de continuar, debe completar los siguientes requisitos:</span>
            </p>
            <div
              class="alert border-0 py-2 px-3 mb-0"
              :class="{
                'alert-info': formValidation.sos_valid && !formValidation.os_valid,
                'alert-danger': !formValidation.sos_valid && !formValidation.os_valid
              }"
              role="alert"
            >
              <div
                v-for="(error, i) in formValidation.errors"
                :key="i"
                class="d-flex align-items-center"
                :class="{ 'mb-2': i < formValidation.errors.length - 1 }"
              >
                <font-awesome-icon :icon="error.icon || ['fas', 'exclamation-circle']" fixed-width />
                <span class="ml-1 fs-14" style="line-height: 14px;">
                  <span>{{ error.message }}</span>
                </span>
              </div>
            </div>
            <div v-if="formValidation.sos_valid && !formValidation.os_valid" class="mt-2 d-flex align-items-center text-primary text-center fs-14">
              <span class="ml-1">NOTA: Si no puede completar los datos ahora, puede registrarla como solicitud de OS y los gestores de Post-venta continuarán con el proceso</span>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="text-center py-2">
            <div class="text-info mx-auto mb-2" style="font-size: 25px;">
              <font-awesome-icon :icon="['far', 'info-circle']" fixed-width size="2xl" />
            </div>
            <p class="text-center mb-1 fs-16 mt-3 text-secondary">
              <span>Se creará la {{ submitAction == 'create_os' ? 'Orden de Servicio en SAP' : 'Solicitud de OS' }}</span>
              <span v-if="submitAction == 'create_sos'">y se notificará a los gestores de Post-venta para que continúen con el proceso</span>
            </p>
          </div>
        </template>
      </template>

      <template #footer>
        <b-button v-if="!creating" :variant="creationStatus === 'success' ? 'outline-danger' : 'outline-info'" @click="onHideSubmitModal">
          <span v-if="creationStatus === 'success'">Cerrar</span>
          <span v-else-if="formValidation.errors.length > 0">Completar los datos</span>
          <span v-else>Continuar con el registro</span>
        </b-button>
        <b-button
          v-if="creationStatus !== 'success'"
          variant="outline-primary"
          :disabled="invalidSubmit || creationStatus == 'success'"
          @click="createServiceCall"
        >
          <span v-if="creationStatus == 'error'">Reintentar</span>
          <span v-else-if="confirmCreateOnResolvedTsStatus == 'pending'">Registrar OS</span>
          <span v-else>{{ submitAction === 'create_os' ? 'Crear Orden de servicio' : 'Registrar solicitud de OS' }}</span>
        </b-button>
      </template>
    </ConfirmModal>
  </div>
</template>

<script>
import Header from '../Header'
import Breadcrumbs from '../Breadcrumbs'
import StepFooter from './StepFooter'
import ConfirmModal from '../../../../components/shared/ConfirmModal'

import FirstStep from './FirstStep'
import SecondStep from './SecondStep'
import ThirdStep from './ThirdStep'

export default {
  name: 'New',
  components: {
    Header,
    Breadcrumbs,
    FirstStep,
    SecondStep,
    ThirdStep,
    StepFooter,
    ConfirmModal
  },
  data() {
    return {
      currentStep: 0,
      showSubmitModal: false,
      submitAction: null,
      creating: false,
      creationStatus: null,
      baseSteps: [
        { label: 'Cliente', step: 0, icon: ['fas', 'user-tie'], done: false },
        { label: 'Servicio', step: 1, icon: ['fas', 'tools'], done: false },
        { label: 'Información adicional', step: 2, icon: ['fas', 'plus'], done: false }
      ],
      form: {
        client: { id: null, client_num: '', name: '', payment_terms: null},
        contact:  { id: null, telephone: '', contact_email: '', solicited_by: '' },
        address:  { id: null, address: '', geo_location: '', assistance_type: 'En sitio' },
        service: {
          sap_series: 61,
          call_type: null,
          subject: '',
          item_serial_no: '',
          comments: '',
          payment_type: null,
          warranty_evaluation_required: null,
          system_type: null,
          problem_type: null,
          is_recurring: null,
          ts_solution: {},
          due_on: null
        },
        attachments: []
      },
      formValidation: {
        os_valid: false,
        sos_valid: false,
        errors: []
      },
      confirmCreateOnResolvedTsStatus: null,
      serviceCallCreated: {}
    }
  },
  computed: {
    invalidSubmit () {
      const { os_valid, sos_valid } = this.formValidation
      if (!os_valid && !sos_valid) return true
      if (this.creating) return true

      return false
    },
    submitButtonLabel () {
      return 'create_os'
    },
    steps () {
      return this.baseSteps.map(step => {
        if (step.step === 0) {
          step.done = this.currentStep > 0
        }
        if (step.step === 1) {
          step.done = this.currentStep > 1
        }
        if (step.step === 2) {
          step.done = this.currentStep > 2
        }

        return step
      })
    }
  },
  mounted() {
    this.scrollToTop()
  },
  methods: {
    goToStep (step) {
      // if (step.step <= this.currentStep) this.currentStep = step.step
      this.currentStep = step.step
    },
    scrollToTop () {
      window.scrollTo({
        top: 70,
        left: 0,
        behavior: 'smooth'
      })
    },
    handleStepClick ({ action, step }) {
      if (action === 'next') {
        if (this.currentStep < this.steps.length - 1) {
          this.currentStep++
          this.scrollToTop()
        }
        return
      }
      if (action === 'prev') {
        if (this.currentStep > 0) {
          this.currentStep--
          this.scrollToTop()
        }
        return
      }
      if (step.step && action === 'dot-clicked') {
        this.currentStep = step.step
        this.scrollToTop()
        return
      }
      if (action === 'reset') {
        this.resetForm()
        return
      }
    },
    async handleCreateOrder () {
      this.formValidation = await this.validateForms()

      this.openSubmitModal()
    },
    // ── Form actions ──
    openSubmitModal () {
      this.creationStatus = null
      this.creating = false
      this.showSubmitModal = true

      if (this.formValidation?.errors?.length > 0) return

      this.createServiceCall()
    },
    onHideSubmitModal () {
      if (this.creationStatus === 'success') {
        window.location.reload()
        return
      }

      if (this.confirmCreateOnResolvedTsStatus == 'pending') {
        this.confirmCreateOnResolvedTsStatus = null
      }

      if (this.creating) {
        this.showSubmitModal = false
        return
      }
      this.showSubmitModal = false
      this.submitAction = null
      this.creating = false
      this.creationStatus = ''
      this.formValidation = []
    },
    createServiceCall () {
      this.creating = true
      this.creationStatus = ''

      let body = this.formatSubmitData()

      this.$http.post('/api/production/service_calls', body)
        .then(response => {
          // console.log(response);
          this.serviceCallCreated = response.data
          if (this.submitAction === 'create_os') {
            this.pingSapStatus()
            return
          }

          this.creationStatus = 'success'
          this.creating = false
        })
        .catch(error => {
          // console.log(error);
          this.creationStatus = 'error'
          this.creating = false
        })
    },
    async pingSapStatus () {
      const MAX_ATTEMPTS = 7
      const INTERVAL_MS = 4000

      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        if (!this.creating) break

        await new Promise(resolve => setTimeout(resolve, INTERVAL_MS))

        try {
          const { data } = await this.$http.get(`/api/production/service_calls/${this.serviceCallCreated.id}/sap_status`)

          const finished = data.sap_is_synced || data.sap_error !== null

          if (!finished) continue

          if (data.sap_is_synced && data.sap_service_call_id) {
            this.serviceCallCreated = {
              ...this.serviceCallCreated,
              sap_is_synced: data.sap_is_synced,
              sap_doc_num: data.sap_doc_num,
              sap_service_call_id: data.sap_service_call_id
            }
            this.onCreateOrderSuccess('create_os')
          } else {
            this.onCreateOrderSuccess('create_sos')
          }

          return
        } catch (error) {
          this.onCreateOrderSuccess('create_sos')
          return
        }
      }

      this.onCreateOrderSuccess('create_sos')
    },
    onCreateOrderSuccess (successAction) {
      this.creationStatus = 'success'
      this.submitAction = successAction
      // this.confirmCreateOnResolvedTsStatus = null
      this.creating = false
    },
    formatSubmitData () {
      const actionMap = {
        create_os: {
          service_step: 'ready_for_sap'
        },
        create_sos: {
          service_step: 'service_call_request'
        }
      }
      const conf = actionMap[this.submitAction]
      if (!conf) return {}

      const { service_step } = conf
      const { photos, documents } = this.formatAttachments()

      let call_type = this.form.service.call_type
      if (this.submitAction === 'create_sos' && !call_type) {
        call_type = 0
      }

      return {
        service_call: {
          // Step 1
          client_code: this.form.client.client_num,
          client_name: this.form.client.name,
          client_id: this.form.client.id,
          solicited_by: this.form.contact.solicited_by,
          contact_person: this.form.contact.solicited_by,
          telephone: this.form.contact.telephone,
          contact_email: this.form.contact.contact_email,
          client_contact_id: this.form.contact.id,
          address: this.form.address.address,
          geo_location: this.form.address.geo_location,
          assistance_type: this.form.address.assistance_type,
          client_address_id: this.form.address.id,
          // Step 2
          status: 'abierto',
          service_step,
          system_type: this.form.service.system_type,
          problem_type_name: this.form.service.problem_type,
          ts_solution: this.form.service.ts_solution,
          // Step 3
          sap_series: this.form.service.sap_series,
          call_type: call_type,
          subject: this.form.service.subject,
          item_serial_no: this.form.service.item_serial_no,
          comments: this.form.service.comments,
          payment_type: this.form.service.payment_type,
          warranty_evaluation_required: this.form.service.warranty_evaluation_required || false,
          is_recurring: this.form.service.is_recurring || false,
          due_on: this.form.service.due_on
        },
        photos,
        documents
      }
    },
    formatAttachments () {
      const { attachments } = this.form
      if (!attachments || attachments.length == 0) return { photos: [], documents: [] }

      const photos = []
      const documents = []
      for (const attachment of attachments) {
        if (attachment.type === 'image') {
          photos.push({
            image: attachment.image,
            caption: attachment.caption
          })
        } else if (attachment.type === 'document') {
          documents.push({
            file: attachment.file,
            name: attachment.name
          })
        }
        // TODO: next version, handle videos
      }

      return { photos, documents }
    },
    resetForm () {
      this.currentStep = 0
      this.form = {
        client: { id: null, client_num: '', name: ''},
        contact:  { id: null, telephone: '', contact_email: '', solicited_by: '' },
        address:  { id: null, address: '', geo_location: '', assistance_type: 'En sitio' },
        service: {
          sap_series: 61,
          call_type: null,
          subject: '',
          item_serial_no: '',
          comments: '',
          payment_type: null,
          warranty_evaluation_required: null,
          system_type: null,
          problem_type: null,
          is_recurring: null,
          ts_solution: {},
          due_on: null
        },
        attachments: []
      },
      this.creating = false
      this.showSubmitModal = false
      this.creationStatus = ''
      this.submitAction = null
      this.formValidation = {
        os_valid: true,
        sos_valid: true,
        errors: []
      }
    },
    async validateForms () {
      await this.$nextTick();

      const errors = []
      const refs = this.$refs || {}

      // first step
      let os_valid = true
      let sos_valid = true
      if (!this.form.client.id || !this.form.contact.id || !this.form.address.id) {
        os_valid = false
      }


      const firstStepFormValid = await refs.firstStep?.checkFormValidity?.()
      if (firstStepFormValid === false) {
        os_valid = false
        sos_valid = false
        errors.push({ message: 'Campos pendientes en Cliente', icon: ['fas', 'user-tie'] });
      }

      // second step
      const secondStepFormValid = await refs.secondStep?.checkFormValidity?.();
      if (secondStepFormValid === false) {
        os_valid = false
        sos_valid = false
        errors.push({ message: 'Campos pendientes en Servicio', icon: ['fas', 'tools'] });
      }

      if (sos_valid) {
        if (!this.form.client.id) {
          errors.push({ message: 'Se requiere que el cliente esté registrado', icon: ['fas', 'user-tie'] })
        }
        if (!this.form.contact.id) {
          errors.push({ message: 'Se requiere seleccionar un contacto registrado', icon: ['far', 'address-card'] })
        }
        if (!this.form.address.id) {
          errors.push({ message: 'Se requiere seleccionar una dirección registrada', icon: ['fas', 'map-marker-alt'] })
        }
        // third step
        const { is_recurring, warranty_evaluation_required } = this.form.service;
        if (is_recurring === null || warranty_evaluation_required === null) {
          os_valid = false
          errors.push({ message: 'Campos pendientes en Información adicional', icon: ['fas', 'plus'] });
        } else {
          const thirdStepFormValid = await refs.thirdStep?.checkFormValidity?.();
          if (thirdStepFormValid === false) {
            os_valid = false
            errors.push({ message: 'Campos pendientes en Información adicional', icon: ['fas', 'plus'] });
          }
        }
      }

      // solved on ts
      const solvedOnTs = this.form.service?.ts_solution?.is_solved === 'Sí' || false
      const validClientForOs = this.form.client?.id > 0 && this.form.address?.id > 0 && this.form.contact?.id > 0

      if (os_valid && sos_valid) {
        this.submitAction = 'create_os'
      } else if (sos_valid && validClientForOs && solvedOnTs) {
        this.submitAction = 'create_os'
      } else {
        this.submitAction = 'create_sos'
      }


      return {
        os_valid,
        sos_valid,
        errors
      }
    },
    handleOrderResolvedFromTs (status) {
      this.confirmCreateOnResolvedTsStatus = status
      if (status === 'pending') {
        this.handleCreateOrder()
      }
    }
  }
}
</script>

<style scoped>
  .main-container {
    background: #F7F7F8;
    color: #1a1a1a;
  }

  .step-body {
    min-height: calc(100vh - 69px);
    padding: 0 20px 28px;
    max-width: 780px;
    margin: 0 auto;
  }

  .card { background: #fff; border: 1px solid #e5e3dc; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.07); }

  .step-fade-enter-active, .step-fade-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
  .step-fade-enter    { opacity: 0; transform: translateX(14px); }
  .step-fade-leave-to { opacity: 0; transform: translateX(-14px); }

  @media (max-width: 640px) {
    .main-container { padding: 0 12px 24px; }
  }
</style>
